import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { CustomerSearch, type SelectedCustomer } from '../components/nalevering/CustomerSearch'
import { ProductSelector, type OrderItem } from '../components/nalevering/ProductSelector'
import { OrderSummary } from '../components/nalevering/OrderSummary'
import clsx from 'clsx'

const steps = [
  { id: 1, label: 'Klant' },
  { id: 2, label: 'Producten' },
  { id: 3, label: 'Verzending & Review' },
]

export function NaleveringPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const prefilled = (location.state as { prefilledCustomer?: SelectedCustomer } | null)?.prefilledCustomer ?? null
  const [step, setStep] = useState(prefilled ? 2 : 1)
  const [customer, setCustomer] = useState<SelectedCustomer | null>(prefilled)
  const [items, setItems] = useState<OrderItem[]>([])
  const [shippingMethod, setShippingMethod] = useState<'postnl' | 'chauffeur'>('postnl')
  const [shippingCost, setShippingCost] = useState<number>(0) // cents
  const [reason, setReason] = useState('nabestelling')
  const [brand, setBrand] = useState<'KLEDA' | 'SCHOONHUISSHOP'>('KLEDA')
  const [kostenMode, setKostenMode] = useState<'gratis' | 'korting' | 'doorberekenen'>('gratis')
  const [discountPercent, setDiscountPercent] = useState(0) // 0-100, alleen relevant bij korting
  const [bevestigingenSturen, setBevestigingenSturen] = useState(true)
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState<{
    werkOrderId: string
    werkOrderName: string
    warning?: string
  } | null>(null)

  const canProceed = (s: number) => {
    if (s === 1) return !!customer
    if (s === 2) return items.length > 0
    return true
  }

  const handleSuccess = (werkOrderId: string, werkOrderName: string, warning?: string) => {
    setResult({ werkOrderId, werkOrderName, warning })
  }

  const reset = () => {
    setStep(1)
    setCustomer(null)
    setItems([])
    setShippingMethod('postnl')
    setShippingCost(0)
    setReason('nabestelling')
    setBrand('KLEDA')
    setKostenMode('gratis')
    setDiscountPercent(0)
    setBevestigingenSturen(true)
    setNotes('')
    setResult(null)
  }

  // Success screen
  if (result) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-main mb-2">Bestelling aangemaakt</h2>
            <p className="text-muted mb-4">
              Order <span className="font-mono font-semibold text-main">{result.werkOrderName}</span> is aangemaakt en staat klaar in de webshop fulfillment queue.
            </p>
            {result.warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
                <p className="text-sm text-amber-800">{result.warning}</p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={reset}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                Nog een bestelling
              </button>
              <button
                onClick={() => navigate('/webshop')}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Naar webshop orders
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-heading font-semibold text-main">Nalevering / Interne Bestelling</h1>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-4 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2 max-w-2xl">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <button
                  onClick={() => canProceed(s.id - 1) || s.id <= step ? setStep(s.id) : undefined}
                  className={clsx(
                    'flex items-center gap-2 text-sm font-medium transition-colors',
                    step === s.id ? 'text-indigo-600' :
                    s.id < step ? 'text-emerald-600 cursor-pointer' :
                    'text-muted'
                  )}
                >
                  <span className={clsx(
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold',
                    step === s.id ? 'bg-indigo-600 text-white' :
                    s.id < step ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-400'
                  )}>
                    {s.id < step ? '✓' : s.id}
                  </span>
                  {s.label}
                </button>
                {i < steps.length - 1 && (
                  <div className={clsx(
                    'flex-1 h-px',
                    s.id < step ? 'bg-emerald-300' : 'bg-slate-200'
                  )} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl">
            {step === 1 && (
              <div>
                <h2 className="text-lg font-semibold text-main mb-1">Klant selecteren</h2>
                <p className="text-sm text-muted mb-4">Zoek een bestaande klant of voeg een nieuwe toe.</p>
                <CustomerSearch value={customer} onChange={setCustomer} />
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-lg font-semibold text-main mb-1">Producten selecteren</h2>
                <p className="text-sm text-muted mb-4">Kies producten uit de Schoonhuisshop catalogus.</p>
                <ProductSelector value={items} onChange={setItems} />
              </div>
            )}

            {step === 3 && customer && (
              <div>
                <h2 className="text-lg font-semibold text-main mb-1">Verzending & Review</h2>
                <p className="text-sm text-muted mb-4">Controleer de bestelling en kies verzendopties.</p>
                <OrderSummary
                  customer={customer}
                  items={items}
                  shippingMethod={shippingMethod}
                  onShippingMethodChange={setShippingMethod}
                  shippingCost={shippingCost}
                  onShippingCostChange={setShippingCost}
                  reason={reason}
                  onReasonChange={setReason}
                  brand={brand}
                  onBrandChange={setBrand}
                  kostenMode={kostenMode}
                  onKostenModeChange={setKostenMode}
                  discountPercent={discountPercent}
                  onDiscountPercentChange={setDiscountPercent}
                  bevestigingenSturen={bevestigingenSturen}
                  onBevestigingenSturenChange={setBevestigingenSturen}
                  notes={notes}
                  onNotesChange={setNotes}
                  onSuccess={handleSuccess}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer nav */}
        {step < 3 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-between">
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:invisible"
            >
              Vorige
            </button>
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed(step)}
              className={clsx(
                'px-6 py-2 text-sm font-medium rounded-lg transition-colors',
                canProceed(step)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              )}
            >
              Volgende
            </button>
          </div>
        )}
      </div>
    </>
  )
}
